// Tone engine. Turns a structured StyleInsight into prose in the user's
// chosen voice. The FACTS are identical across tones (same axis, same
// numbers, same fix); only the delivery changes.
//
// - professional: measured, plainspoken, no jokes.
// - coach: warm, encouraging, frames it as a choice not a mistake.
// - playful: a PRODUCTIVE roast. It only gets spicy when a pick genuinely
//   clashes (severity "strong"); on smaller deviations it stays light. It
//   always hands back a fix, so the humor never costs the user information.
//
// Never didactic. These are nudges, not a lecture: no "you must", no
// exactness theater. A pick that fits the vibe produces no insight at all
// (styleInsight returns null upstream), so tone is only ever applied to
// things actually worth mentioning.

import type { Tone } from "./types.js";
import type { StyleInsight, InsightDirection } from "./style.js";

// ---- Safety gate -----------------------------------------------------------
// What a finding is ABOUT decides whether it may be delivered in a non-neutral
// voice. Taste is fair game for coach or roast. Safety, durability, and budget
// are always plain and professional: a joke must never bury the one thing the
// user actually needed to hear (mold-prone finish, a blown budget, a warranty
// killer). This is the guardrail that keeps roast mode shippable.
export type FindingKind = "taste" | "coherence" | "safety" | "durability" | "budget";

const HUMOR_ALLOWED = new Set<FindingKind>(["taste", "coherence"]);

export function isCritical(kind: FindingKind): boolean {
  return !HUMOR_ALLOWED.has(kind);
}

// The tone that actually applies once the gate has its say. Critical kinds
// are forced to professional no matter what the user picked.
export function effectiveTone(requested: Tone, kind: FindingKind): Tone {
  return HUMOR_ALLOWED.has(kind) ? requested : "professional";
}

// The constructive fix for each direction, shared by every tone.
const FIX: Record<InsightDirection, string> = {
  darker: "Lighten the tone, or lean into the moodier room on purpose.",
  brighter: "Go a few shades deeper, or add contrast to hold the drama.",
  cooler: "Warm it with nearby wood or brass, or commit to the cool palette.",
  warmer: "Pull it cooler, or balance it with a few cool accents.",
  busier: "Quiet the pattern, or let it be the one busy thing in the room.",
  flatter: "Layer texture elsewhere so the room does not fall flat.",
};

// Plain consequence of the deviation, direction-aware.
const CONSEQUENCE: Record<InsightDirection, string> = {
  darker: "the room will read cozier and more enclosed than that look implies",
  brighter: "the room will read more open and airy, and less grounded",
  cooler: "it can feel crisp, or a touch clinical next to warm materials",
  warmer: "it feels cozier, but gives up some of the clean, cool edge",
  busier: "it will pull the eye and read more active",
  flatter: "it is calm, but you lose some of the texture that style layers",
};

function lightnessNoun(direction: InsightDirection, lrv: number): string {
  return `at LRV ${lrv}`;
}

// professional / coach share a factual frame with different framing verbs.
function measured(i: StyleInsight, warm: boolean): string {
  const where = i.axis === "lightness" ? ` ${lightnessNoun(i.direction, i.value)}` : "";
  const lead = warm
    ? `You are close. ${i.name}${where} runs ${i.magnitude} ${i.direction}`
    : `${i.name}${where} runs ${i.magnitude} ${i.direction}`;
  const ref = i.axis === "lightness" ? ` than ${i.styleLabel} usually goes (around LRV ${i.target})`
    : ` than ${i.styleLabel} usually leans`;
  const tail = warm
    ? `If you want that look, ${FIX[i.direction][0].toLowerCase()}${FIX[i.direction].slice(1)} If you are leaning the other way, this is a good pick.`
    : `${cap(CONSEQUENCE[i.direction])}. ${FIX[i.direction]}`;
  return `${lead}${ref}. ${tail}`;
}

// playful: dialed to severity. Strong picks get a real roast; notable ones
// get a wink. Always closes with the fix.
function playful(i: StyleInsight): string {
  const where = i.axis === "lightness" ? ` at LRV ${i.value}` : "";
  if (i.severity === "strong") {
    const jab = ROASTS[i.direction] ?? `is a long way from ${i.styleLabel}`;
    return `${i.name}${where} ${jab} You picked ${i.styleLabel}; this is not that. ${FIX[i.direction]}`;
  }
  return `Cute, but ${i.name}${where} nudges ${i.magnitude} ${i.direction} of the ${i.styleLabel} brief. Not a crime. ${FIX[i.direction]}`;
}

const ROASTS: Record<InsightDirection, string> = {
  darker: "brings full basement energy to a look that is supposed to feel sunlit.",
  brighter: "is floodlit where the vibe wanted candlelight.",
  cooler: "reads like a dentist office next to anything warm.",
  warmer: "is toasting a palette that asked to stay crisp.",
  busier: "has more going on than the whole rest of the room combined.",
  flatter: "is flatter than a parking lot where the style wanted texture.",
};

function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

// Style insights are taste by default, so any tone is fair. Pass a kind to
// route a finding through the safety gate (e.g. a durability note stays
// professional even in roast mode).
export function renderInsight(
  i: StyleInsight,
  tone: Tone,
  kind: FindingKind = "taste"
): string {
  switch (effectiveTone(tone, kind)) {
    case "playful": return playful(i);
    case "coach": return measured(i, true);
    case "professional":
    default: return measured(i, false);
  }
}
